/**
 *  @class
 *  @function BackgroundVideo
 */
if (!customElements.get('background-video')) {
	class BackgroundVideo extends HTMLElement {
	  constructor() {
			super();

			this.tl = null;
			this.splittext = null;
			this._observer = null;
	  }
		connectedCallback() {
			if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
				this.prepareAnimations();
			}
			this.observeVideo();
		}
		observeVideo() {
			var video_container = this.querySelector('.background-video__iframe');
			if (!video_container) return;

			var template = video_container.querySelector('.background-video__deferred');
			if (!template) return;

			// A full-bleed video looping behind the content is the largest moving thing on the
			// site, and nothing in the theme ever asked whether the visitor wanted it: the
			// autoplay lives in the embed URL, which no CSS rule can reach (GLB-007). The
			// guidance is explicit about full-viewport moving backgrounds, so under the setting
			// the section stays on its fallback image and the video is never stamped in at all.
			//
			// Without a fallback image there would be nothing left to look at, so there the
			// video is stamped but told not to play, which leaves the platform's own still
			// frame. Read at stamp time rather than at load, so someone who changes the setting
			// gets the answer they asked for on the next section they scroll to.
			var stillPreferred = function () {
				return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			};
			var hasFallback = !!this.querySelector('.background-video__fallback-img img');

			var self = this;
			this._observer = new IntersectionObserver(function(entries) {
				if (entries[0].isIntersecting) {
					self._observer.disconnect();
					self._observer = null;

					if (stillPreferred() && hasFallback) {
						self.classList.add('background-video--still');
						return;
					}

					var stamped = template.content.cloneNode(true);
					if (stillPreferred()) {
						// No fallback image to fall back to: keep the frame, drop the motion.
						stamped.querySelectorAll('iframe').forEach(function (frame) {
							frame.src = frame.src.replace(/([?&])autoplay=1/, '$1autoplay=0')
								.replace(/([?&])background=1/, '$1background=0&autoplay=0');
						});
						stamped.querySelectorAll('video').forEach(function (el) {
							el.removeAttribute('autoplay');
							el.removeAttribute('loop');
						});
					}
					template.replaceWith(stamped);

					// iOS Safari does not honor the `autoplay` attribute on
					// <video> elements inserted into the DOM by script — call
					// play() explicitly after insertion. (Iframe-based videos
					// like YouTube/Vimeo handle autoplay via URL params.)
					// Stripping the attributes above is not enough on its own: this explicit
					// play() exists precisely because iOS ignores the attribute, so it would
					// have started the video the setting just asked us to leave still.
					var video = stillPreferred() ? null : video_container.querySelector('video');
					if (video) {
						video.muted = true;
						video.playsInline = true;
						var playPromise = video.play();
						if (playPromise && typeof playPromise.catch === 'function') {
							playPromise.catch(function() { /* autoplay blocked */ });
						}
					}
				}
			}, { rootMargin: '100px' });

			this._observer.observe(this);
		}
		disconnectedCallback() {
			if (this._observer) {
				this._observer.disconnect();
				this._observer = null;
			}
			if (this.tl) {
				this.tl.kill();
				this.tl = null;
			}
			if (this.splittext) {
				this.splittext.revert();
				this.splittext = null;
			}
		}
		prepareAnimations() {
			let section = this,
					button_offset = 0;

			section.tl = gsap.timeline({
				scrollTrigger: {
					trigger: section,
					start: "top center"
				}
			});

			document.fonts.ready.then(function() {
				section.splittext = new SplitText(section.querySelectorAll('h3, p'), {
						type: 'lines, words',
						linesClass: 'line-child'
					}
				);

				if (section.querySelector('h3')) {
					let h3_duration = 0.7 + ((section.querySelectorAll('h3 .line-child div').length - 1) * 0.05);
					section.tl
						.from(section.querySelectorAll('h3 .line-child div'), {
							duration: h3_duration,
							yPercent: '100',
							stagger: 0.05
						}, 0);
					button_offset += h3_duration;
				}
				if (section.querySelector('p')) {
					let p_duration = 0.7 + ((section.querySelectorAll('p .line-child div').length - 1) * 0.02);
					section.tl
						.from(section.querySelectorAll('p .line-child div'), {
							duration: p_duration,
							yPercent: '100',
							stagger: 0.02
						}, 0);
					button_offset += p_duration;
				}
				if (section.querySelector('.video-lightbox-modal__button')) {
					section.tl
						.fromTo(section.querySelector('.video-lightbox-modal__button'), {
							autoAlpha: 0
						}, {
							duration: 0.5,
							autoAlpha: 1
						}, button_offset * 0.4);
				}

			});
		}
	}
	customElements.define('background-video', BackgroundVideo);
}
